// How the worklist reads the events the server sends (TGCLOUD-1899). PURE: no React, no fetch.
//
// One event is one family's record of one change. The worklist shows one ROW PER VARIANT (for
// GenCC, per gene–disease pair and submitter), with every family holding it beneath, because the
// question a release raises is "which of my families hold this variant", asked once.
//
// Classifications are the sources' own words, shown verbatim beside each other. Nothing here ranks
// them, scores them or picks one: `direction` is the server's description of which way the SOURCE
// moved, and a human decides what it means for each family.

// The direction labels, in English; translated where drawn.
export const DIRECTION_LABELS = Object.freeze({
  escalation: 'Towards pathogenic',
  deEscalation: 'Away from pathogenic',
  added: 'Newly classified',
  withdrawn: 'No longer confirmed',
  lateral: 'Wording changed'
});

export const SOURCE_LABELS = Object.freeze({
  clinvar: 'ClinVar',
  gencc: 'GenCC'
});

// The MUI chip colour for a direction. Not a clinical colour (src/theme/palette.js): the chip says
// which way the source moved, never how severe the variant is.
export const DIRECTION_CHIP_COLOUR = Object.freeze({
  escalation: 'warning',
  deEscalation: 'info',
  added: 'info',
  withdrawn: 'default',
  lateral: 'default'
});

const timeOf = (value) => (value ? new Date(value).getTime() : 0);

/** The key every family's event for the same change shares. */
export function groupKeyOf(event) {
  if (!event) return '';
  if (event.source === 'gencc') {
    return [
      'gencc',
      event.gene || '',
      event.diseaseId || '',
      event.submitter || ''
    ].join('|');
  }
  if (event.clinvarVariationId) return `clinvar|${event.clinvarVariationId}`;
  return [
    'clinvar',
    event.gene || '',
    event.variantC || '',
    event.variantG || '',
    event.variantP || ''
  ].join('|');
}

const personKeyOf = (event) =>
  `${String(event.user_id || '')}|${String(event.familyMemberId || '')}`;

/**
 * Group events by change: one group per variant (or GenCC pair), newest change first, each with
 * one row per person holding it (the newest event for that person).
 *
 * @param {object[]} events as GET /variant-reclassification returns them
 * @returns {Array<{ key: string, source: string, gene: string|null, variantC: string|null,
 *   variantP: string|null, clinvarVariationId: string|null, diseaseId: string|null,
 *   diseaseName: string|null, submitter: string|null, direction: string,
 *   previousClassification: string|null, newClassification: string|null, detectedAt: string,
 *   sourceDatasetVersion: string|null, people: object[], eventIds: string[] }>}
 */
export function groupEvents(events) {
  const groups = new Map();
  for (const event of Array.isArray(events) ? events : []) {
    if (!event) continue;
    const key = groupKeyOf(event);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        source: event.source || 'clinvar',
        gene: event.gene || event.record?.gene || null,
        variantC: event.variantC || event.record?.variantC || null,
        variantP: event.variantP || event.record?.variantP || null,
        clinvarVariationId: event.clinvarVariationId || null,
        diseaseId: event.diseaseId || null,
        diseaseName: event.diseaseName || null,
        submitter: event.submitter || null,
        direction: event.direction,
        previousClassification: event.previousClassification ?? null,
        newClassification: event.newClassification ?? null,
        detectedAt: event.detectedAt,
        sourceDatasetVersion: event.sourceDatasetVersion || null,
        people: [],
        eventIds: []
      };
      groups.set(key, group);
    }
    // The group's headline is its newest event.
    if (timeOf(event.detectedAt) > timeOf(group.detectedAt)) {
      group.direction = event.direction;
      group.previousClassification = event.previousClassification ?? null;
      group.newClassification = event.newClassification ?? null;
      group.detectedAt = event.detectedAt;
      group.sourceDatasetVersion = event.sourceDatasetVersion || null;
    }
    group.eventIds.push(String(event._id));
    const pk = personKeyOf(event);
    const held = group.people.find((p) => p.personKey === pk);
    if (!held) {
      group.people.push({ personKey: pk, ...event });
    } else if (timeOf(event.detectedAt) > timeOf(held.detectedAt)) {
      Object.assign(held, event);
    }
  }
  return [...groups.values()].sort(
    (a, b) => timeOf(b.detectedAt) - timeOf(a.detectedAt)
  );
}

/** ClinVar's own page for a variation id, where the clinician checks the source. */
export function clinvarUrl(variationId) {
  if (!variationId) return null;
  return `https://www.ncbi.nlm.nih.gov/clinvar/variation/${encodeURIComponent(
    String(variationId)
  )}/`;
}

/** GenCC's own page for a gene. */
export function genccUrl(gene) {
  if (!gene) return null;
  return `https://search.thegencc.org/genes?search=${encodeURIComponent(
    String(gene)
  )}`;
}

/** Does the group mention the text (gene, HGVS, disease, submitter), case-insensitively? */
export function matchesFilter(group, text) {
  const needle = String(text || '')
    .trim()
    .toLowerCase();
  if (!needle) return true;
  return [
    group.gene,
    group.variantC,
    group.variantP,
    group.diseaseName,
    group.submitter,
    group.clinvarVariationId
  ].some((v) => v && String(v).toLowerCase().includes(needle));
}

/** The variant as the row names it: gene and HGVS, or the gene and disease for a GenCC pair. */
export function groupTitle(group) {
  if (group.source === 'gencc') {
    return [group.gene, group.diseaseName || group.diseaseId]
      .filter(Boolean)
      .join(' · ');
  }
  return [group.gene, group.variantC || group.variantP]
    .filter(Boolean)
    .join(' ');
}
